// src/pages/platform/PlatformListPage.tsx

import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { usePlatform, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardContent,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';
import type { PlatformStatus } from '@core/types/platform';
import { PlatformCreateDialog } from '@/features/platform/PlatformCreateDialog';

type StatusFilter = 'ALL' | PlatformStatus;

const STATUS_TABS: { id: StatusFilter; labelKey: string }[] = [
  { id: 'ALL', labelKey: 'platform.status.all' },
  { id: 'PLATFORM_STATUS_ACTIVE', labelKey: 'platform.status.active' },
  { id: 'PLATFORM_STATUS_PENDING', labelKey: 'platform.status.pending' },
  { id: 'PLATFORM_STATUS_SUSPENDED', labelKey: 'platform.status.suspended' },
  { id: 'PLATFORM_STATUS_MAINTENANCE', labelKey: 'platform.status.maintenance' },
  { id: 'PLATFORM_STATUS_ARCHIVED', labelKey: 'platform.status.archived' },
];

function mapStatusToVariant(
  status: string,
): 'success' | 'warning' | 'error' | 'primary' | 'secondary' {
  switch (status) {
    case 'PLATFORM_STATUS_ACTIVE':
      return 'success';
    case 'PLATFORM_STATUS_PENDING':
      return 'warning';
    case 'PLATFORM_STATUS_SUSPENDED':
      return 'error';
    case 'PLATFORM_STATUS_MAINTENANCE':
      return 'primary';
    case 'PLATFORM_STATUS_ARCHIVED':
    case 'PLATFORM_STATUS_DELETED':
      return 'secondary';
    default:
      return 'secondary';
  }
}

function formatStatusLabel(status: string): string {
  return status
    .replace('PLATFORM_STATUS_', '')
    .charAt(0)
    .toUpperCase()
    + status
        .replace('PLATFORM_STATUS_', '')
        .slice(1)
        .toLowerCase();
}

function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '--';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return String(dateStr);
  }
}

/**
 * Platform list page with status filter tabs, search, and paginated table.
 */
export const PlatformListPage = observer(() => {
  const store = usePlatform();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [activeStatus, setActiveStatus] = useState<StatusFilter>(
    (searchParams.get('status') as StatusFilter) || 'ALL',
  );
  const [statusMenuOpen, setStatusMenuOpen] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const fetchPlatforms = useCallback(() => {
    const params: Record<string, any> = {
      pagination: {
        cursor: searchParams.get('cursor') || '',
        limit: 20,
        direction: 'forward',
      },
      sort: [],
      date_range: {} as any,
      filters: {} as any,
    };

    const searchValue = searchParams.get('search');
    if (searchValue) {
      params.search = searchValue;
    }

    const statusValue = searchParams.get('status');
    if (statusValue && statusValue !== 'ALL') {
      params.status = statusValue;
    }

    store.ListPlatforms(params);
  }, [searchParams]);

  useEffect(() => {
    fetchPlatforms();
  }, [fetchPlatforms]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params: Record<string, string> = {};
    if (search) params.search = search;
    if (activeStatus !== 'ALL') params.status = activeStatus;
    setSearchParams(params);
  };

  const handleStatusFilter = (status: StatusFilter) => {
    setActiveStatus(status);
    const params: Record<string, string> = {};
    if (search) params.search = search;
    if (status !== 'ALL') params.status = status;
    setSearchParams(params);
  };

  const handleLoadMore = () => {
    if (store.pageInfo?.next_cursor) {
      const params: Record<string, string> = {
        cursor: store.pageInfo.next_cursor,
      };
      if (search) params.search = search;
      if (activeStatus !== 'ALL') params.status = activeStatus;
      setSearchParams(params);
    }
  };

  const handleSetStatus = async (itemId: string, newStatus: PlatformStatus) => {
    setStatusMenuOpen(null);
    try {
      await store.SetPlatformStatus(itemId, { status: newStatus } as any);
      fetchPlatforms();
    } catch {
      // Error handled by store
    }
  };

  const handleDelete = async (itemId: string) => {
    if (!confirm(t('platform.deleteConfirm'))) return;
    try {
      await store.DeletePlatform(itemId);
    } catch {
      // Error handled by store
    }
  };

  const statusOptions: PlatformStatus[] = [
    'PLATFORM_STATUS_ACTIVE',
    'PLATFORM_STATUS_PENDING',
    'PLATFORM_STATUS_SUSPENDED',
    'PLATFORM_STATUS_MAINTENANCE',
    'PLATFORM_STATUS_ARCHIVED',
  ];

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="sr-only">{t('platform.list')}</h1>
        <Button className="sm:ml-auto" onClick={() => setShowCreate(true)}>{t('platform.create')}</Button>
      </div>

      {/* Status filter tabs */}
      <div className="border-b border-border">
        <div className="flex gap-0 -mb-px">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleStatusFilter(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeStatus === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              {t(tab.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4">
            <Input
              placeholder={t('platform.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth={false}
              className="flex-1"
            />
            <Button type="submit" variant="secondary">
              {t('common.search')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Platform table */}
      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('platform.field.name')}</TableHead>
              <TableHead>{t('platform.field.slug')}</TableHead>
              <TableHead>{t('platform.field.status')}</TableHead>
              <TableHead>{t('platform.field.owner')}</TableHead>
              <TableHead>{t('platform.field.createdAt')}</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : store.items.length === 0 ? (
              <TableEmpty colSpan={6}>
                <span className="table-empty-message">{t('platform.noItems')}</span>
                <p className="mt-1 text-sm text-muted-foreground">{t('platform.listDescription')}</p>
              </TableEmpty>
            ) : (
              store.items.map((item: any) => {
                const itemId = item.entity?.id || item.id;
                return (
                  <TableRow key={itemId || Math.random()}>
                    <TableCell>
                      <Link
                        to={`/platforms/${itemId}`}
                        className="text-primary hover:underline font-medium"
                      >
                        {String(item.name ?? '')}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground font-mono">
                        {String(item.slug ?? '')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={mapStatusToVariant(item.status)} size="sm">
                        {formatStatusLabel(item.status || '')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {String(item.owner?.display_name || item.owner?.name || '--')}
                    </TableCell>
                    <TableCell>
                      {formatDate(item.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2 relative">
                        <Link to={`/platforms/${itemId}`}>
                          <Button variant="ghost" size="sm">
                            {t('common.view')}
                          </Button>
                        </Link>
                        <Link to={`/platforms/${itemId}/edit`}>
                          <Button variant="ghost" size="sm">
                            {t('common.edit')}
                          </Button>
                        </Link>
                        {/* Status change dropdown */}
                        <div className="relative">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              setStatusMenuOpen(statusMenuOpen === itemId ? null : itemId)
                            }
                          >
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                              />
                            </svg>
                          </Button>
                          {statusMenuOpen === itemId && (
                            <>
                              <div
                                className="fixed inset-0 z-10"
                                onClick={() => setStatusMenuOpen(null)}
                              />
                              <div className="absolute right-0 top-full mt-1 z-20 w-48 rounded-md border border-border bg-card shadow-lg py-1">
                                {statusOptions
                                  .filter((s) => s !== item.status)
                                  .map((status) => (
                                    <button
                                      key={status}
                                      onClick={() => handleSetStatus(itemId, status)}
                                      className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-2"
                                    >
                                      <Badge variant={mapStatusToVariant(status)} size="sm">
                                        {formatStatusLabel(status)}
                                      </Badge>
                                    </button>
                                  ))}
                                <div className="border-t border-border my-1" />
                                <button
                                  onClick={() => {
                                    setStatusMenuOpen(null);
                                    handleDelete(itemId);
                                  }}
                                  className="w-full text-left px-3 py-2 text-sm text-destructive hover:bg-muted transition-colors"
                                >
                                  {t('common.delete')}
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination */}
      {store.hasMore && !store.isLoading && (
        <div className="flex justify-center">
          <Button variant="secondary" onClick={handleLoadMore}>
            {t('common.loadMore')}
          </Button>
        </div>
      )}

      <PlatformCreateDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(id) => {
          fetchPlatforms();
          if (id) navigate(`/platforms/${id}`);
        }}
      />
    </div>
  );
});

export default PlatformListPage;
